import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Star } from 'lucide-react';
import '../css/EstablishmentDetail.css';
import usePageTitle from '../hooks/usePageTitle';
import { getSingleEstablishment } from '../helpers/establishments';
import { API_BASE_URL } from '../helpers/api';
import { getReviewsByEstablishment, getReviewsStatsForEstablishment } from '../helpers/reviews';
import ReviewSection from '../components/ReviewSection';
import { useAuth } from '../context/AuthContext';
import { addToFavorites, checkIsFavorite, deleteFavorite } from '../helpers/favorites';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
import L from 'leaflet';
import { renderHTML } from '../hooks/tools';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const EstablishmentDetail = () => {
  const { id } = useParams();
  const { isAuthenticated, user } = useAuth();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [establishment, setEstablishment] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setisLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      // Guard clause to ensure both user and establishment exist and have IDs
      if (!user?.id || !establishment?.id) {
        console.warn('Missing required user or establishment data');
        setIsFavorite(false);
        setFavoriteId(null);
        return;
      }

      try {
        const response = await checkIsFavorite(user.id, establishment.id);

        // Safely set state with fallback values
        setIsFavorite(response?.isFavorite ?? false);
        setFavoriteId(response?.id ?? null);
      } catch (error) {
        console.error('Error checking favorite status:', error);
        // Set safe fallback values in case of error
        setIsFavorite(false);
        setFavoriteId(null);
      }
    };

    // Only run if we have authentication and user data
    if (isAuthenticated && user) {
      checkFavoriteStatus();
    } else {
      // Reset states if not authenticated or no user
      setIsFavorite(false);
      setFavoriteId(null);
    }
  }, [establishment, isAuthenticated, user]);

  const fetchData = async () => {
    try {
      setisLoading(true);
      const resEst = await getSingleEstablishment(id);
      const resRev = await getReviewsByEstablishment(id);
      const reviewsStat = await getReviewsStatsForEstablishment(id);
      setEstablishment(resEst);
      setReviews(resRev);
      setStats(reviewsStat);
    } catch (e) {
      console.error(e);
    } finally {
      setisLoading(false);
    }
  };

  usePageTitle(establishment ? establishment.name : 'Établissement non trouvé');

  const handleFavoriteToggle = async () => {
    try {
      if (isFavorite) {
        const response = await deleteFavorite(favoriteId);
        if (response) setIsFavorite(!isFavorite);
      } else {
        const response = await addToFavorites({ data: { user: user.id, establishment: id } });
        if (response.success) {
          setIsFavorite(!isFavorite);
          setFavoriteId(response.id);
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setIsFavorite(isFavorite);
    }
  };

  const groupOpeningHours = (hours) => {
    // Group days by their hours
    const grouped = hours.reduce((acc, curr) => {
      if (!acc[curr.hours]) {
        acc[curr.hours] = [];
      }
      acc[curr.hours].push(curr.day);
      return acc;
    }, {});

    // Convert to array format with days joined
    return Object.entries(grouped).map(([hours, days]) => {
      if (days.length === 1) {
        return { days: days[0], hours };
      }

      // If consecutive days, show as range
      if (days.length > 2) {
        const firstDay = days[0];
        const lastDay = days[days.length - 1];
        return { days: `${firstDay} - ${lastDay}`, hours };
      }

      // If just two days or non-consecutive, join with comma
      return { days: days.join(', '), hours };
    });
  };

  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Chargement...</p>
      </div>
    );
  }

  if (!establishment || !reviews) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          Établissement non trouvé
        </div>
      </div>
    );
  }

  const coordinates = [
    parseFloat(establishment.location.lat),
    parseFloat(establishment.location.long)
  ];

  return (
    <div className="establishment-detail">
      <div className="image-gallery">
        <div className="main-image">
          <img
            src={API_BASE_URL + establishment.images[activeImageIndex].url}
            alt={establishment.name}
          />
          <div className="image-overlay">
            <h1>{establishment.name}</h1>
            {isAuthenticated && (
              <button
                className="favorite-btn-hero"
                onClick={handleFavoriteToggle}
                aria-label="Toggle favorite"
              >
                <i className={`bi bi-heart${isFavorite ? '-fill' : ''}`}></i>
                <span>{isFavorite ? 'Favori' : 'Ajouter aux favoris'}</span>
              </button>
            )}
            <div className="badges d-flex gap-2">
              <span className="category-badge">
                {establishment.category.name}
              </span>
              <span className="price-badge">
                {establishment.price}
              </span>
            </div>
          </div>
        </div>
        <div className="thumbnail-strip">
          {establishment?.images?.map((image, index) => (
            <div
              key={index}
              className={`thumbnail ${index === activeImageIndex ? 'active' : ''}`}
              onClick={() => setActiveImageIndex(index)}
            >
              <img src={API_BASE_URL + image.url} alt={`View ${index + 1}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="container py-4">
        <div className="row">
          {/* Column 1: Rating and Description */}
          <div className="col-md-8">
            <div className="info-card">
              <div className="rating-section">
                <div className="rating">
                  <span className="rating-score">{stats.average}<small>/5</small></span>
                  <div className="stars">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`bi bi-star-fill ${i < Math.floor(stats.average) ? 'active' : ''}`}
                      ></i>
                    ))}
                  </div>
                </div>
                <span className="review-count">{stats.count} avis</span>
              </div>
              <div className="features">
                {establishment.tags.map((tag, index) => (
                  <span key={index} className="feature-tag">{tag.name}</span>
                ))}
              </div>
              <div className="description">{renderHTML(establishment.description)}</div>
            </div>
          </div>

          {/* Column 2: Contact */}
          <div className="col-md-4">
            <div className="info-card">
              <div className="contact-info">
                <h3>Informations</h3>

                <div className="info-item">
                  <i className="bi bi-geo-alt-fill"></i>
                  <div>
                    <h4>Adresse</h4>
                    <p>{establishment.address}, {establishment.postalCode} {establishment.city}</p>
                  </div>
                </div>

                <div className="info-item">
                  <i className="bi bi-clock-fill"></i>
                  <div style={{ width: '100%' }}>
                    <h4>Horaires</h4>
                    {groupOpeningHours(establishment?.openingHours).map((schedule, index) => (
                      <div key={index} className="schedule">
                        <span className="day">{schedule.days}</span>
                        <span className="hours">{schedule.hours}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {(establishment.phone || establishment.email || establishment.website) && (
                  <div className="info-item">
                    <i className="bi bi-telephone-fill"></i>
                    <div>
                      <h4>Contact</h4>
                      {establishment.phone && (
                        <p>Téléphone : {establishment.phone}</p>
                      )}
                      {establishment.email && (
                        <p>E-mail : {establishment.email}</p>
                      )}
                      {establishment.email && (
                        <p>E-mail : {establishment.email && establishment.email}</p>
                      )}
                      {establishment.website && (
                        <a href={`${establishment.website}`} target="_blank" rel="noopener noreferrer">
                          {establishment.website}
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="info-card">
              <h3>Localisation</h3>
              <div className="map-preview">
                <div className="map-container">
                  <MapContainer
                    center={coordinates}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={coordinates}>
                      <Popup>
                        <div className="map-popup">
                          <h3>{establishment.name}</h3>
                          <p>{establishment.address}</p>
                          <div className="popup-meta">
                            <span className="category">{establishment.category.name}</span>
                            <span className="rating">
                              <Star size={14} />
                              {stats.average} ({stats.count} avis)
                            </span>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${establishment.location.lat},${establishment.location.long}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-map-btn"
                >
                  <i className="bi bi-map"></i>
                  Voir sur Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        {isAuthenticated ? (
          <ReviewSection reviews={reviews} establishmentId={id} userId={user.id} isAuthenticated={isAuthenticated} />
        ) : (
          <ReviewSection reviews={reviews} establishmentId={id} />
        )}

      </div>
    </div>
  );
};

export default EstablishmentDetail;